<template>
  <!--
    AB#21620 — the right-side drawer shell the admin portal opens instead of a
    centre modal (Users details/create, Roles, permissions, grants). One place
    owns the two conventions that kept regressing: the panel sits BELOW the app
    bar (top: var(--app-bar-offset)), never behind it, and every colour is a
    theme token so light and dark both hold. Scrim, ×, or Esc closes it.

    Slots: default → the scrolling body; `subhead` → a sticky band under the
    header (a search / filter bar); `footer` → the sticky action row.
  -->
  <Teleport to="body">
    <div class="dr-scrim" data-test="dr-scrim" :style="{ zIndex: scrimZ }" @click="onScrim"></div>
    <aside
      class="dr"
      role="dialog"
      aria-modal="true"
      :aria-label="ariaLabel || title"
      :data-test="dataTest"
      :data-depth="depth"
      :data-form="form ? 'true' : undefined"
      :style="{ '--dr-width': width, zIndex: scrimZ + 1 }"
      tabindex="-1"
      @keydown.esc="onEsc"
      @input="onEdit"
      @change="onEdit"
      @click.capture="onInnerClick"
    >
      <header class="dr-head">
        <div class="dr-ident">
          <!-- Callers with a richer heading (e.g. a title + an inline note) can
               override via #title; most just pass the title/subtitle props. -->
          <slot name="title">
            <h2 class="dr-title" data-test="dr-title">{{ title }}</h2>
            <span v-if="subtitle" class="dr-sub" data-test="dr-subtitle">{{ subtitle }}</span>
          </slot>
        </div>
        <button
          ref="closeBtn"
          type="button"
          class="dash-icon-btn dr-close"
          title="Close"
          aria-label="Close"
          data-test="dr-close"
          @click="requestClose"
        >
          <XMarkIcon class="size-5" />
        </button>
      </header>

      <div v-if="$slots.subhead" class="dr-subhead">
        <slot name="subhead" />
      </div>

      <div class="dr-body">
        <slot />
      </div>

      <footer v-if="$slots.footer" class="dr-foot">
        <slot name="footer" />
      </footer>
    </aside>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, provide, ref } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { drawerStack } from '@/components/Shared/drawerStack';
import { DRAWER_GUARD, useFormGuard } from '@/components/Shared/drawerGuard';

/**
 * AB#21912 — drawers stack. Price breakdown is a drawer and opens Add product,
 * Add manual line and the remake questionnaire as drawers on top of itself; a
 * second drawer must sit above the first AND its scrim must dim the first, or
 * the two read as side by side and the first one's fields stay clickable.
 * Each open drawer takes the next pair of layers above the base (10035/10036),
 * released when it closes. Two levels of nesting stay under the desk-message
 * host (10040); see the layer scale below.
 */
const DRAWER_BASE_Z = 10035;

const props = withDefaults(
  defineProps<{
    title: string;
    subtitle?: string | null;
    ariaLabel?: string;
    /** Panel width; capped to the viewport. */
    width?: string;
    dataTest?: string;
    /**
     * The drawer holds a form (ticket 9782). A click outside it then does NOT
     * close it, and ×, Esc and a Cancel button ask before discarding anything
     * that was typed or picked. Read-only panes leave this off and keep
     * closing on an outside click.
     */
    form?: boolean;
  }>(),
  { subtitle: null, ariaLabel: '', width: '460px', dataTest: 'drawer', form: false }
);

const emit = defineEmits<{ (e: 'close'): void }>();

const closeBtn = ref<HTMLButtonElement | null>(null);
const depth = ref(drawerStack.open++);
const scrimZ = computed(() => DRAWER_BASE_Z + depth.value * 2);
onBeforeUnmount(() => {
  drawerStack.open = Math.max(0, drawerStack.open - 1);
});

/**
 * AB#21826 — Esc closes the drawer, but not when the person is closing
 * something inside it: an open date picker or select dropdown takes the key
 * itself, and a form must never vanish under a half-filled field.
 */
const onEsc = (e: KeyboardEvent) => {
  if (e.defaultPrevented) return;
  const t = e.target as HTMLElement | null;
  if (t?.closest?.('.dp__main, .vs--open, [aria-expanded="true"], .swal2-container')) return;
  void requestClose();
};

/**
 * Ticket 9782 — a form must not vanish under what somebody typed: see
 * useFormGuard. A click outside a form does nothing; ×, Esc and the form's own
 * Cancel ask before discarding.
 */
// `dirty` is deliberately NOT read by the template. A browser runs pending
// renders between two listeners of one event, so a re-render of this shell on
// the first keystroke would write the field's old value back before the
// field's own v-model listener had stored the new one (the first character
// typed was lost). For the same reason the edit listeners are on the way up.
const { dirty, onEdit, onInnerClick, mayClose, guard } = useFormGuard({
  enabled: () => props.form,
  isTop: () => depth.value === drawerStack.open - 1,
  ownClose: () => closeBtn.value,
});

const requestClose = async () => {
  if (await mayClose()) emit('close');
};

const onScrim = () => {
  // A form is closed on purpose only: ×, Esc or its own buttons.
  if (props.form) return;
  emit('close');
};

provide(DRAWER_GUARD, guard);
// markClean: a form that saves without closing (Price breakdown) says so, or × asks
// about edits that are already saved.
defineExpose({ requestClose, mayClose, dirty, markClean: guard.markClean });

onMounted(() => {
  try {
    closeBtn.value?.focus();
  } catch {
    /* no document (tests) */
  }
});
</script>

<style lang="scss">
/* Unscoped: sections a caller drops into the body inherit the drawer's chrome. */

/*
  AB#21862 — the drawer sits ABOVE the centre-modal layer (.modal-backdrop,
  10030), not below it.

  A drawer is only ever opened *from* something: from the page, or — since
  AB#21826 — from inside a modal (Price breakdown opens Add manual line, Add
  shipping and discount, Add product and the remake questionnaire as drawers).
  Nothing in the app opens a modal from inside a drawer, so the drawer is always
  the newer, topmost surface and there is no case where a modal has to cover one.
  At 10021 the drawer landed under the Price breakdown modal and the form was
  unreachable (Zendesk #9513).

  The layer scale this shell participates in, low to high:

    10020/10021  detail panes (DetailsPane, UserDetailsPane) — page furniture
    10025-10028  sidebar flyouts
    10030        centre modals (.modal-backdrop)
    10035/10036  drawers  <- here (a drawer opened from a drawer takes
                 10037/10038, the next 10039/10040 — AB#21912)
    10040/10041  desk messages host
    10050/10051  WhatsApp attachment
    10060/10061  teleported popups (Select, Dropdown, Datepicker) — they must
                 clear every surface that can contain one, drawers included
    10070        Ask Atlas launcher (AB#21165 — above every drawer)
    11111        swal2 confirms and toasts

  Move this and the popup layer together: the menus are teleported to <body>,
  so they escape the drawer's stacking context and compete with it directly.
*/
.dr-scrim {
  position: fixed;
  /* The app bar (53px, plus the impersonation banner when docked) stays visible
     and clickable above the drawer; the drawer owns the content area only. */
  top: var(--app-bar-offset, 53px);
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10035;
  background: rgba(20, 22, 26, 0.28);
}
.dr {
  position: fixed;
  top: var(--app-bar-offset, 53px);
  right: 0;
  bottom: 0;
  z-index: 10036;
  width: min(100vw, var(--dr-width, 460px));
  display: flex;
  flex-direction: column;
  background: var(--surface);
  box-shadow: -12px 0 32px rgba(15, 18, 24, 0.14);
  color: var(--ink);
  animation: dr-in 0.18s ease-out;
}
@keyframes dr-in {
  from {
    transform: translateX(24px);
    opacity: 0;
  }
  to {
    transform: none;
    opacity: 1;
  }
}
@media (prefers-reduced-motion: reduce) {
  .dr {
    animation: none;
  }
}
.dr-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 16px 14px 20px;
  border-bottom: 1px solid var(--border);
}
.dr-ident {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.dr-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.25;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.dr-sub {
  font-size: 12.5px;
  color: var(--ink-soft);
  overflow-wrap: anywhere;
}
.dr-close {
  flex-shrink: 0;
}
.dr-subhead {
  flex-shrink: 0;
  padding: 12px 20px;
  border-bottom: 1px solid var(--border);
  background: var(--surface-2);
}
.dr-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 18px 20px;
}
.dr-foot {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  /* Reserve the bottom-right for the Zendesk "Ask Atlas" launcher, which is
     fixed to the viewport corner and floats above every drawer (AB#21165 — the
     launcher must stay reachable, so content makes room for it rather than
     fighting z-index). ~64px launcher + its margin, so the primary action never
     comes to rest underneath it. This shell backs every drawer built on it, so
     the clearance is inherited by all of them, this one place to change. */
  padding: 14px 92px 14px 20px;
  border-top: 1px solid var(--border);
  background: var(--surface);
  flex-wrap: wrap;
}
@media (max-width: 480px) {
  /* Phone: the launcher clearance would eat half the row; let actions wrap
     onto two rows and keep the clearance as bottom padding instead. */
  .dr-foot {
    padding: 12px 16px 84px;
    /* A tall footer (Price breakdown carries order type, memo and note there)
       must not swallow the body on a phone: it scrolls on its own instead. */
    max-height: 55vh;
    overflow-y: auto;
  }
}
</style>
